import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { patchCachedRows, restoreCachedRows } from '../../lib/optimistic';
import {
  fetchSectionStatuses,
  saveSectionStatuses,
  addProjectMember,
  createProject,
  createProjectCategory,
  ensurePersonalProject,
  fetchProjectCategories,
  fetchActiveTapPeople,
  fetchProjectTemplates,
  fetchProjectWorkspace,
  fetchTemplateBlueprintCounts,
  fetchProjects,
  archiveProject,
  removeProjectMember,
  updateProject,
  updateProjectMemberRole,
} from './queries';
import type {
  CreateProjectInput,
  GrantableRole,
  Project,
  ProjectListItem,
  ProjectRole,
  ProjectWorkspace,
} from './types';

/**
 * Server state for the project foundation. TanStack Query is the only server
 * state layer; there are no Realtime subscriptions and nothing is prefetched
 * at application startup.
 */

export const projectKeys = {
  all: ['projects'] as const,
  list: () => [...projectKeys.all, 'list'] as const,
  detail: (projectId: string) => [...projectKeys.all, 'detail', projectId] as const,
  templates: () => [...projectKeys.all, 'templates'] as const,
  people: () => [...projectKeys.all, 'tap-people'] as const,
};

/**
 * Which status columns each section draws, for one project.
 *
 * Keyed on the project, not the section: the board switches tabs constantly
 * and one cached map beats a fetch per tab.
 */
export function useSectionStatuses(projectId: string) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...projectKeys.all, 'section-statuses', projectId],
    queryFn: () => fetchSectionStatuses(client, projectId),
    enabled: access.status === 'approved' && projectId !== '',
    retry: false,
  });
}

export function useSaveSectionStatuses(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sectionId: string; statusIds: string[] }) =>
      saveSectionStatuses(client, input.sectionId, input.statusIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...projectKeys.all, 'section-statuses', projectId],
      });
    },
  });
}

/** The signed-in user's id, or an empty string when access is not approved. */
function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useProjectCategories() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...projectKeys.all, 'categories'] as const,
    queryFn: () => fetchProjectCategories(client),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useCreateProjectCategory() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (name: string) => createProjectCategory(client, { name, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'categories'] });
    },
  });
}

export function usePersonalProject() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...projectKeys.all, 'personal'] as const,
    queryFn: () => ensurePersonalProject(client),
    enabled: access.status === 'approved',
    staleTime: Infinity,
    retry: false,
  });
}

export function useUpdateProject(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: Parameters<typeof updateProject>[2]) =>
      updateProject(client, projectId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

export function useArchiveProject(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => archiveProject(client, projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useProjects() {
  const { client } = useAuth();
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => fetchProjects(client),
  });
}

export function useTemplateBlueprintCounts() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...projectKeys.templates(), 'blueprints'] as const,
    queryFn: () => fetchTemplateBlueprintCounts(client),
  });
}

export function useProjectTemplates() {
  const { client } = useAuth();
  return useQuery({
    queryKey: projectKeys.templates(),
    queryFn: () => fetchProjectTemplates(client),
  });
}

export function useProjectWorkspace(projectId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: projectKeys.detail(projectId ?? ''),
    queryFn: () => fetchProjectWorkspace(client, projectId as string),
    enabled: Boolean(projectId),
    // Authorization is re-resolved by the request, so a stale authorized
    // result must never satisfy a later load.
    retry: false,
  });
}

/** Loaded only for managers, when the members panel can actually add people. */
export function useTapPeople(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: projectKeys.people(),
    queryFn: () => fetchActiveTapPeople(client),
    enabled,
    retry: false,
  });
}

export function useAddProjectMember(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const addedBy = useActorId();
  return useMutation({
    mutationFn: (input: { userId: string; projectRole: GrantableRole }) =>
      addProjectMember(client, { projectId, ...input, addedBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useUpdateProjectMemberRole(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, projectRole }: { memberId: string; projectRole: ProjectRole }) =>
      updateProjectMemberRole(client, memberId, projectRole),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useRemoveProjectMember(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => removeProjectMember(client, memberId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useCreateProject(onCreated: (project: Project) => void) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(client, input),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.list() });
      onCreated(project);
    },
  });
}

/**
 * Moving a project between category headings on the list.
 *
 * Category is free text on the project rather than a foreign key, so the drag
 * writes a name. Optimistic: the row lands under the new heading immediately
 * and returns if the write is refused.
 *
 * Page-level rather than bound to one project id, because the drag starts on
 * a row and lands on a heading, and nothing on that page is scoped to a single
 * project.
 */
export function useMoveProjectCategory() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; category: string }) =>
      updateProject(client, input.projectId, { category: input.category }),
    onMutate: (input) =>
      patchCachedRows<ProjectListItem>(queryClient, projectKeys.list(), [
        { id: input.projectId, changes: { category: input.category } },
      ]),
    onError: (_error, _input, context) => {
      restoreCachedRows(queryClient, projectKeys.list(), context);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

/**
 * Handing a project to somebody else.
 *
 * One RPC, because the handoff is several writes — owner_id, the old owner's
 * membership, the new owner's membership — and a client doing them separately
 * would leave a project with two owners, or none, if any step failed.
 *
 * The optimistic patch touches the header's owner only. The membership rows
 * are refetched instead: the handoff demotes one and may create another, and
 * guessing both here would be reimplementing the function in the browser —
 * with the header briefly disagreeing with the members list if the guess were
 * wrong.
 */
export function useHandOverProject(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newOwnerId: string) => {
      const { error } = await client.rpc('hand_over_project', {
        p_project: projectId,
        p_new_owner: newOwnerId,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onMutate: async (newOwnerId) => {
      const key = projectKeys.detail(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      // The detail cache holds the whole workspace, not a bare project.
      const previous = queryClient.getQueryData<ProjectWorkspace>(key);
      if (previous) {
        queryClient.setQueryData<ProjectWorkspace>(key, {
          ...previous,
          project: { ...previous.project, owner_id: newOwnerId },
        });
      }
      return { previous };
    },
    onError: (_error, _newOwnerId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(projectKeys.detail(projectId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

export function handOverErrorMessage(raw: string): string {
  if (raw.includes('project_owner_must_be_active_tap_user')) {
    return 'That person cannot own a project. Owners must be active TAP users.';
  }
  if (raw.includes('not_authorized_to_hand_over_project')) {
    return 'Only the project owner or a TAP admin can hand a project over.';
  }
  return 'The project could not be handed over. Try again.';
}
